// src/config/database/lfJobTypesConfig.js

const AREA_CURVE_DEFAULTS = {
  mode: 'area_curve',
  group_by: 'job_type_and_materials',
  area_basis: 'raw_area_sqm',
  start_sqm: 5,
  end_sqm: 30,
  max_discount_percent: 10,
  curve: 'linear'
};

const parseVolumeDiscountConfig = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return value;
    }
  }
  return value;
};

const serializeVolumeDiscountConfig = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  return JSON.stringify(value);
};

const renderAreaCurveEditor = (field, value, formData, handleInputChange) => {
  const parsedValue = parseVolumeDiscountConfig(value);
  const isLegacyArray = Array.isArray(parsedValue);
  const isAreaCurve = parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue);
  const curveConfig = isAreaCurve ? { ...AREA_CURVE_DEFAULTS, ...parsedValue } : AREA_CURVE_DEFAULTS;

  const updateCurveField = (key, nextValue) => {
    handleInputChange(field.key, {
      ...curveConfig,
      [key]: nextValue
    });
  };

  const startSqm = Number(curveConfig.start_sqm) || 0;
  const endSqm = Number(curveConfig.end_sqm) || 0;
  const maxDiscountPercent = Number(curveConfig.max_discount_percent) || 0;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-900">Area-Curve Volume Discount</div>
            <div className="text-sm text-gray-600">
              Matches the small-format discount editor style, but uses grouped sqm instead of sheets.
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleInputChange(field.key, { ...AREA_CURVE_DEFAULTS })}
              className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Enable Curve
            </button>
            <button
              type="button"
              onClick={() => handleInputChange(field.key, '')}
              className="rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>

        {isLegacyArray ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-medium text-amber-900">Legacy quantity tiers detected</div>
            <p className="mt-1 text-sm text-amber-800">
              This job type still uses the older quantity-tier format. You can keep it, or replace it with the sqm area curve below.
            </p>
            <textarea
              value={JSON.stringify(parsedValue, null, 2)}
              onChange={(e) => handleInputChange(field.key, e.target.value)}
              rows={6}
              className="mt-3 w-full rounded-md border border-amber-300 px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ) : isAreaCurve ? (
          <>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Start Area (sqm)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      End Area (sqm)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Max Discount (%)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <tr>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={curveConfig.start_sqm}
                        onChange={(e) => updateCurveField('start_sqm', e.target.value === '' ? '' : Number(e.target.value))}
                        className="w-28 rounded border border-gray-300 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={curveConfig.end_sqm}
                        onChange={(e) => updateCurveField('end_sqm', e.target.value === '' ? '' : Number(e.target.value))}
                        className="w-28 rounded border border-gray-300 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={curveConfig.max_discount_percent}
                          onChange={(e) => updateCurveField('max_discount_percent', e.target.value === '' ? '' : Number(e.target.value))}
                          className="w-24 rounded border border-gray-300 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Grouping</div>
                <div className="mt-1 text-sm text-gray-800">Same job type + same materials</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Area Basis</div>
                <div className="mt-1 text-sm text-gray-800">Raw piece area before pricing rounding</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Curve Type</div>
                <div className="mt-1 text-sm text-gray-800">Linear from {startSqm} to {endSqm} sqm</div>
              </div>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <h4 className="text-sm font-semibold text-blue-900">How this curve works</h4>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-blue-900">
                <li>0% discount up to {startSqm} sqm</li>
                <li>Linear increase between {startSqm} sqm and {endSqm} sqm</li>
                <li>Capped at {maxDiscountPercent}% from {endSqm} sqm onward</li>
                <li>Discount applies to print, materials, and only flagged finishing run-on costs</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
            No large-format area curve configured for this job type.
          </div>
        )}
      </div>
    </div>
  );
};

export const lfJobTypesTableConfig = {
  // Table identification
  id: 'lf_job_types',
  name: 'Large Format Job Types',
  apiEndpoint: '/api/admin/large-format/job-types',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      width: '30%',
      render: (value) => (
        <span className="font-mono text-sm text-gray-600">{value}</span>
      )
    },
    {
      key: 'name',
      label: 'Job Type Name',
      sortable: true,
      width: '50%',
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'updated_at',
      label: 'Last Modified',
      sortable: true,
      width: '20%',
      render: (value) => (
        <span className="text-gray-500 text-sm">
          {value ? new Date(value).toLocaleDateString() : '-'}
        </span>
      )
    }
  ],

  // Field definitions for the RecordModal form
  fields: [
    {
      key: 'name',
      label: 'Job Type Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., Vinyl Signs, Banners, Posters, Display Boards',
      help: 'A descriptive name for this large format job type. The ID will be generated automatically from this name.'
    },
    {
      key: 'volume_discount_tiers',
      label: 'Volume Discount Tiers',
      type: 'conditional',
      required: false,
      renderField: renderAreaCurveEditor,
      help: 'Configure the grouped sqm volume discount for this large-format job type. Existing legacy quantity tiers are still supported.'
    }
  ]
};

// Transform functions for API communication
export const transformLfJobTypeForApi = (formData) => {
  const payload = {
    name: formData.name
  };

  // Include volume_discount_tiers if provided
  if (formData.volume_discount_tiers) {
    payload.volume_discount_tiers = serializeVolumeDiscountConfig(formData.volume_discount_tiers);
  }

  return payload;
};

export const transformLfJobTypeFromApi = (apiData) => {
  const formData = {
    name: apiData.name || ''
  };

  if (apiData.volume_discount_tiers) {
    try {
      formData.volume_discount_tiers = typeof apiData.volume_discount_tiers === 'string'
        ? JSON.parse(apiData.volume_discount_tiers)
        : apiData.volume_discount_tiers;
    } catch (e) {
      formData.volume_discount_tiers = apiData.volume_discount_tiers;
    }
  } else {
    formData.volume_discount_tiers = '';
  }

  return formData;
};

// Validation rules
export const validateLfJobType = (data) => {
  const errors = {};

  if (!data.name || data.name.trim() === '') {
    errors.name = 'Job type name is required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'Job type name must be at least 2 characters long';
  } else if (data.name.trim().length > 100) {
    errors.name = 'Job type name must be less than 100 characters';
  }

  // Validate volume_discount_tiers if provided
  const parsedVolumeDiscountConfig = parseVolumeDiscountConfig(data.volume_discount_tiers);

  if (parsedVolumeDiscountConfig) {
    try {
      const tiers = typeof parsedVolumeDiscountConfig === 'string'
        ? JSON.parse(parsedVolumeDiscountConfig)
        : parsedVolumeDiscountConfig;

      if (Array.isArray(tiers)) {
        for (let i = 0; i < tiers.length; i++) {
          const tier = tiers[i];

          if (!tier.min_qty || !tier.max_qty || tier.discount_percent === undefined) {
            errors.volume_discount_tiers = `Tier ${i}: Missing required fields (min_qty, max_qty, discount_percent)`;
            break;
          }

          if (tier.min_qty < 1) {
            errors.volume_discount_tiers = `Tier ${i}: min_qty must be >= 1`;
            break;
          }

          if (tier.max_qty < tier.min_qty) {
            errors.volume_discount_tiers = `Tier ${i}: max_qty must be >= min_qty`;
            break;
          }

          if (tier.discount_percent < 0 || tier.discount_percent > 100) {
            errors.volume_discount_tiers = `Tier ${i}: discount_percent must be 0-100`;
            break;
          }
        }
      } else if (tiers && typeof tiers === 'object') {
        if (tiers.mode !== 'area_curve') {
          errors.volume_discount_tiers = "Area-curve config must use mode 'area_curve'";
        } else if (
          tiers.start_sqm === undefined ||
          tiers.end_sqm === undefined ||
          tiers.max_discount_percent === undefined
        ) {
          errors.volume_discount_tiers = 'Area-curve config requires start_sqm, end_sqm, and max_discount_percent';
        } else if (Number(tiers.start_sqm) < 0) {
          errors.volume_discount_tiers = 'Area-curve start_sqm must be >= 0';
        } else if (Number(tiers.end_sqm) <= Number(tiers.start_sqm)) {
          errors.volume_discount_tiers = 'Area-curve end_sqm must be greater than start_sqm';
        } else if (
          Number(tiers.max_discount_percent) < 0 ||
          Number(tiers.max_discount_percent) > 100
        ) {
          errors.volume_discount_tiers = 'Area-curve max_discount_percent must be 0-100';
        } else if ((tiers.curve || 'linear') !== 'linear') {
          errors.volume_discount_tiers = "Only 'linear' area curves are currently supported";
        }
      } else {
        errors.volume_discount_tiers = 'Must be a JSON array or area-curve object';
      }
    } catch (e) {
      errors.volume_discount_tiers = 'Invalid JSON format';
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

// Common large format job type templates for quick creation
export const LF_JOB_TYPE_TEMPLATES = [
  { name: 'Vinyl Signs' },
  { name: 'Banners' },
  { name: 'Posters' },
  { name: 'Display Boards' },
  { name: 'Window Graphics' },
  { name: 'Wall Graphics' },
  { name: 'Vehicle Graphics' },
  { name: 'Floor Graphics' },
  { name: 'Exhibition Graphics' },
  { name: 'Outdoor Signage' },
  { name: 'Trade Show Displays' },
  { name: 'Fabric Printing' },
  { name: 'Canvas Prints' },
  { name: 'Backlit Graphics' },
  { name: 'Mesh Banners' }
];

export default lfJobTypesTableConfig;
